import React, { useState, useEffect } from "react";
import { useCredits } from "./hooks/useCredits";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import { Search, Wallet, Trash2, AlertCircle, Edit } from "lucide-react";
import EditCreditModal from "./components/EditCreditModal";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0
  );

function CreditsTab() {
  const { credits, isLoading, fetchCredits, deleteCredit, updateCredit } = useCredits();
  const [searchTerm, setSearchTerm] = useState("");
  const [editingCredit, setEditingCredit] = useState(null);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const filteredCredits = credits.filter(
    (c) =>
      c.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone.includes(searchTerm)
  );

  const totalCreditsValue = filteredCredits.reduce(
    (acc, curr) => acc + curr.value,
    0
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-green-600" /> Gestão de Créditos
          </h2>
          <p className="text-muted-foreground text-sm">
            Gerencie saldos gerados por cancelamentos.
          </p>
        </div>

        {/* Card de Resumo Rápido */}
        <div className="bg-green-50 dark:bg-card/20 border border-green-100 dark:border-border p-3 rounded-lg flex flex-col items-end">
          <span className="text-xs text-muted-foreground uppercase font-bold">
            Total em Aberto
          </span>
          <span className="text-2xl font-bold text-green-700 dark:text-muted-foreground">
            {formatCurrency(totalCreditsValue)}
          </span>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou telefone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Desktop View - Table */}
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>Validade</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Carregando créditos...
                </TableCell>
              </TableRow>
            ) : filteredCredits.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  Nenhum crédito encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredCredits.map((credit) => {
                const isExpired =
                  credit.expirationDate && new Date() > credit.expirationDate;

                return (
                  <TableRow
                    key={credit.id}
                    className={isExpired ? "bg-red-50 dark:bg-red-900/10" : ""}
                  >
                    <TableCell className="font-medium">
                      {credit.clientName}
                    </TableCell>
                    <TableCell>{credit.phone}</TableCell>
                    <TableCell className="font-bold text-green-600 dark:text-muted-foreground">
                      {formatCurrency(credit.value)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {credit.expirationDate
                          ? credit.expirationDate.toLocaleDateString("pt-BR")
                          : "-"}
                        {isExpired && (
                          <span className="text-xs font-bold text-red-500 flex items-center gap-1 border border-red-200 bg-red-100 px-1.5 rounded">
                            <AlertCircle className="h-3 w-3" /> Vencido
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                          onClick={() => setEditingCredit(credit)}
                          title="Editar informações do crédito"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => deleteCredit(credit.id)}
                          title="Dar baixa / Utilizar crédito"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile View - Cards */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {isLoading ? (
          <div className="text-center py-8 bg-white dark:bg-accent rounded-lg border">
            Carregando créditos...
          </div>
        ) : filteredCredits.length === 0 ? (
          <div className="text-center py-8 bg-white dark:bg-accent rounded-lg border text-muted-foreground">
            Nenhum crédito encontrado.
          </div>
        ) : (
          filteredCredits.map((credit) => {
            const isExpired = credit.expirationDate && new Date() > credit.expirationDate;
            
            return (
              <div 
                key={credit.id} 
                className={`flex flex-col bg-white dark:bg-accent rounded-xl border dark:border-border shadow-sm overflow-hidden ${isExpired ? "border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-900/10" : ""}`}
              >
                {/* Card Header & Body */}
                <div className="p-4 flex flex-col gap-2">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-lg text-slate-800 dark:text-foreground line-clamp-1">{credit.clientName}</h3>
                    {isExpired && (
                      <span className="text-xs font-bold text-red-500 flex items-center gap-1 border border-red-200 bg-red-100 px-2 py-0.5 rounded-full shrink-0">
                        <AlertCircle className="h-3 w-3" /> Vencido
                      </span>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-end mt-1">
                    <div className="flex flex-col gap-1 text-sm text-slate-600 dark:text-muted-foreground">
                      <span className="flex items-center gap-1"><strong className="font-medium">Tel:</strong> {credit.phone}</span>
                      <span className="flex items-center gap-1">
                        <strong className="font-medium">Validade:</strong>{" "}
                        {credit.expirationDate ? credit.expirationDate.toLocaleDateString("pt-BR") : "-"}
                      </span>
                    </div>
                    <div className="text-xl font-black text-green-600 dark:text-muted-foreground shrink-0">
                      {formatCurrency(credit.value)}
                    </div>
                  </div>
                </div>

                {/* Card Footer Actions */}
                <div className="bg-slate-50 dark:bg-card/50 p-3 border-t dark:border-border flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 bg-white dark:bg-accent text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200 dark:border-blue-900/50"
                    onClick={() => setEditingCredit(credit)}
                  >
                    <Edit className="h-4 w-4 mr-2" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 bg-white dark:bg-accent text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 dark:border-red-900/50"
                    onClick={() => deleteCredit(credit.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Baixar
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <EditCreditModal
        isOpen={!!editingCredit}
        onClose={() => setEditingCredit(null)}
        onSave={updateCredit}
        creditData={editingCredit}
      />
    </div>
  );
}

export default CreditsTab;
